// Extraction on screen (Godot scripts/islands/extraction_bell.gd + extraction_sequence.gd):
//   - the BELL: the Godot bell model floating over the extraction islet's deck under a brass yoke,
//     a halo ring and a tall warm light beam so it's findable from the arena. Shoot it: it swings
//     and clangs (the sim rings it, Ev.Bell). It stays in full colour through the grey wash.
//   - the TRAIN: a short rake of the real space train (loco, tender, the saloon car, the tail)
//     flies in over the 30 s holdout, decelerates and docks alongside the islet with its saloon
//     door at the boarding point; after the last call it pulls away up into the sky.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { World } from '../../../sim/world';
import { BELL_R, bellPos, boardPos } from '../../../sim/run';
import type { ViewCtx } from '../contracts';
import { Train } from '../world/train';
import type { CarType } from '../world/train_cars';
import { withHaze } from '../materials';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const BELL = BASE + 'assets/run/bell.glb';
const RAKE: CarType[] = ['loco', 'tender', 'saloon', 'tail'];
const DOOR_CAR = 2;
/** how far the rake's axis sits out past the boarding point (half a car's width and a step) */
const SIDE_OFF = 5.5;

const BEAM_VERT = /* glsl */ `
varying vec2 vUv; varying vec3 vN; varying vec3 vV;
void main() {
  vUv = uv;
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vN = normalize( mat3( modelMatrix ) * normal ); vV = normalize( cameraPosition - wp.xyz );
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const BEAM_FRAG = /* glsl */ `
varying vec2 vUv; varying vec3 vN; varying vec3 vV;
uniform float uTime; uniform float uOn; uniform vec3 uCol;
void main() {
  float side = pow( abs( dot( vN, vV ) ), 1.5 );
  float fall = pow( 1.0 - vUv.y, 1.6 );
  float rip = 0.75 + 0.25 * sin( vUv.y * 60.0 - uTime * 5.0 );
  float a = side * fall * rip * uOn * 0.85;
  gl_FragColor = vec4( uCol * a, a );
}
`;

export class ExtractView {
  readonly group = new THREE.Group();
  private bellPivot = new THREE.Group();
  private bellRoot = new THREE.Group();
  private halo: THREE.Mesh;
  private haloMat: THREE.MeshBasicMaterial;
  private beam: THREE.Mesh;
  private beamU: Record<string, THREE.IUniform>;
  private light: THREE.PointLight;
  private swing = 0; private swingV = 0; private swingDir = 1;
  private bellOn = 0;
  private rung = false;
  private train: Train | null = null;
  private trainK = 0;
  private ctx: ViewCtx;
  private world: World;
  private disposables: { dispose(): void }[] = [];
  private bellReady = false;
  /** the bell and the train have arrived (the game warms them hidden: client/render/warm.ts) */
  get loaded() { return this.bellReady && (this.train?.loaded ?? true); }
  private bp: { x: number; y: number; z: number };
  private dp: { x: number; y: number; z: number; ux: number; uz: number };

  constructor(ctx: ViewCtx, time: { value: number }) {
    this.ctx = ctx;
    this.world = ctx.world;
    this.group.name = 'extraction';
    this.group.visible = false;
    ctx.scene.add(this.group);
    this.bp = bellPos(ctx.world);
    this.dp = boardPos(ctx.world);
    // the extraction train is built with the island (in frame-budget slices) and kept hidden, so the
    // moment it's called nothing loads
    this.train = new Train(this.world.seed ^ 0x7a11, this.ctx, RAKE);
    this.train.group.visible = false;
    this.group.add(this.train.group);

    // ---- the bell on its yoke
    this.bellRoot.position.set(this.bp.x, this.bp.y, this.bp.z);
    this.group.add(this.bellRoot);
    const brassDark = withHaze(new THREE.MeshStandardMaterial({ color: 0x6e4f1c, metalness: 0.8, roughness: 0.42 }), 0.3);
    const yoke = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.22, 0.3), brassDark);
    yoke.position.y = BELL_R + 0.55;
    yoke.castShadow = true;
    this.bellRoot.add(yoke);
    for (const s of [-1, 1]) {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.2, 12, 8), brassDark);
      cap.position.set(s * 1.32, BELL_R + 0.55, 0);
      this.bellRoot.add(cap);
      this.disposables.push(cap.geometry);
    }
    this.disposables.push(yoke.geometry, brassDark);
    this.bellPivot.position.y = BELL_R + 0.45;
    this.bellRoot.add(this.bellPivot);
    new GLTFLoader().load(BELL, (g) => {
      const m = g.scene;
      // the generated bell is ~1 m tall, mouth down; hang it under the pivot at the sim's hit size
      const box = new THREE.Box3().setFromObject(m);
      const h = box.max.y - box.min.y || 1;
      const s = (BELL_R * 2.1) / h;
      m.scale.setScalar(s);
      m.position.y = -box.max.y * s;
      m.traverse((o) => {
        const mm = o as THREE.Mesh;
        if (!mm.isMesh) return;
        mm.castShadow = true;
        const mat = mm.material as THREE.MeshStandardMaterial;
        // polished brass, self-lit: it has to be the brightest warm thing through the grey wash
        mat.color.set(0xf0bb55);
        mat.metalness = 0.55; mat.roughness = 0.3;
        mat.emissive = new THREE.Color(0.42, 0.24, 0.05);
        mat.emissiveMap = null;
        withHaze(mat, 0.15);
        this.disposables.push(mat, mm.geometry);
        if (mat.map) this.disposables.push(mat.map);
      });
      this.bellPivot.add(m);
      this.bellReady = true;
    }, undefined, (e) => { this.bellReady = true; console.warn('[bell] model failed', e); });

    // halo ring framing it, and the light
    this.haloMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.55, 0.2).multiplyScalar(2.2), transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
    this.halo = new THREE.Mesh(new THREE.TorusGeometry(BELL_R + 0.55, 0.1, 8, 48), this.haloMat);
    this.halo.position.y = 0;
    this.bellRoot.add(this.halo);
    this.disposables.push(this.halo.geometry, this.haloMat);
    this.light = new THREE.PointLight(0xff9a40, 0, 14, 2);
    this.light.position.y = 0;
    this.bellRoot.add(this.light);

    // the beam: from the deck up into the sky
    this.beamU = { uTime: time, uOn: { value: 0 }, uCol: { value: new THREE.Color(1.0, 0.62, 0.28) } };
    const bg = new THREE.CylinderGeometry(0.5, 0.9, 140, 20, 1, true);
    bg.translate(0, 70, 0);
    const bm = new THREE.ShaderMaterial({ vertexShader: BEAM_VERT, fragmentShader: BEAM_FRAG, uniforms: this.beamU, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.beam = new THREE.Mesh(bg, bm);
    this.beam.position.set(this.bp.x, this.bp.y - 3.2, this.bp.z);
    this.beam.frustumCulled = false;
    this.group.add(this.beam);
    this.disposables.push(bg, bm);
  }

  /** the sim rang it */
  ring() {
    this.swingV += 3.2 * this.swingDir;
    this.swingDir = -this.swingDir;
    this.rung = true;
  }

  /**
   * show: extraction is on (bell up); train: 0 none, 0..1 arriving over the holdout, 1 docked,
   * > 1 leaving.
   */
  update(dt: number, time: number, show: boolean, called: boolean, train: number) {
    this.group.visible = show || train > 0;
    this.bellOn += ((show ? 1 : 0) - this.bellOn) * (1 - Math.exp(-dt * 3));
    // bell: pendulum
    this.swingV += (-this.swing * 18 - this.swingV * 1.6) * dt;
    this.swing += this.swingV * dt;
    this.bellPivot.rotation.z = this.swing;
    this.bellRoot.position.y = this.bp.y + Math.sin(time * 1.3) * 0.12;
    this.bellRoot.visible = this.bellOn > 0.02;
    const pulse = 0.75 + 0.25 * Math.sin(time * (called ? 2 : 5));
    this.haloMat.opacity = this.bellOn * pulse;
    // the halo stands upright and turns to face you, framing the bell like a target ring
    this.halo.lookAt(this.ctx.camera.position.x, this.bellRoot.position.y, this.ctx.camera.position.z);
    this.light.intensity = this.bellOn * (called ? 18 : 30) * pulse;
    this.beamU.uOn.value = this.bellOn * (called ? 0.45 : 1);
    void this.rung;

    // train (made with the island, hidden until it's called: see the constructor)
    if (this.train) this.train.group.visible = train > 0 && train < 2.4;
    if (this.train && train > 0) {
      this.trainK = train;
      // heading along the islet rim (perpendicular to the outward normal), door car at the boarding point
      const fx = -this.dp.uz, fz = this.dp.ux;
      let off = 0;
      for (let i = 0; i < DOOR_CAR; i++) off += this.train.carLength(i);
      off += this.train.carLength(DOOR_CAR) / 2;
      const ax = this.dp.x + this.dp.ux * SIDE_OFF + fx * off, az = this.dp.z + this.dp.uz * SIDE_OFF + fz * off;
      this.train.setDock(ax, this.dp.y + 3.4, az, fx, fz, Math.max(0, this.trainK));
      this.train.update(dt, time);
    }
  }

  /** world point where the boarding door is (for the HUD marker) */
  doorPos() { return this.dp; }
  bellPos() { return this.bp; }

  dispose() {
    this.group.parent?.remove(this.group);
    this.train?.dispose();
    for (const d of this.disposables) d.dispose();
  }
}
